"use client"

import React, { useEffect, useRef } from "react"
import QRCodeStyling from "qr-code-styling"
import { buildQrOptions } from "../qr-style"

const LOGO_PATH = "/logo.svg"

export interface QRCodeProps {
  value: string
  size?: number
  className?: string
  // Defaults to this app's own /logo.svg. Override when the QR's destination is a *different*
  // app than the one rendering it (e.g. a Carbonomex page linking into the ForestSync registry)
  // — the embedded mark should always match where scanning it actually takes you, not where
  // it's displayed.
  logoSrc?: string
}

/**
 * The one styled QR code every app renders — forest/timber gradient, rounded dots, embedded
 * logo. Shared here (not duplicated per app) specifically so a QR code looks the same whether
 * it's on a registry passport, a transaction record, or a merchant's dashboard: same brand mark
 * everywhere a QR points somewhere real. Each consuming app needs its own /logo.svg in public/
 * for the embedded logo to resolve.
 */
export const QRCode: React.FC<QRCodeProps> = ({ value, size = 192, className, logoSrc = LOGO_PATH }) => {
  const ref = useRef<HTMLDivElement>(null)
  const qrRef = useRef<QRCodeStyling | null>(null)
  const isInitializedRef = useRef(false)

  useEffect(() => {
    if (!ref.current) return

    // Prevent multiple initializations in StrictMode
    if (isInitializedRef.current) return

    // Clean up any existing content
    ref.current.innerHTML = ""

    // Create new instance
    qrRef.current = new QRCodeStyling({
      width: size,
      height: size,
      data: value,
      ...buildQrOptions(logoSrc),
    })

    qrRef.current.append(ref.current)
    isInitializedRef.current = true

    return () => {
      if (ref.current) {
        ref.current.innerHTML = ""
      }
      qrRef.current = null
      isInitializedRef.current = false
    }
  }, []) // Empty dependency array - only run once

  // Handle value/size/logo changes separately
  useEffect(() => {
    if (qrRef.current && ref.current) {
      qrRef.current.update({
        data: value,
        width: size,
        height: size,
        ...buildQrOptions(logoSrc),
      })
    }
  }, [value, size, logoSrc])

  return <div ref={ref} className={className} />
}
